<template>
    <form class="user-data-form-container" @submit.prevent="submitForm">
        <h1 class="title">{{ $t("personalDataForm.title") }}</h1>
        <!-- First name -->
        <div v-if="qrcodeObject?.getUserFirstName !== 'no'" class="form-field">
            <input
                id="first-name-input"
                type="text"
                v-model="firstName"
                :placeholder="firstNameLabel"
                @blur="onFieldBlur('firstName')"
                @keydown="clearFieldError('firstName')"
            />
            <div v-if="errors?.firstName?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.firstName) }}
            </div>
        </div>

        <!-- Last name -->
        <div v-if="qrcodeObject?.getUserLastName !== 'no'" class="form-field">
            <input
                id="last-name-input"
                type="text"
                v-model="lastName"
                :placeholder="lastNameLabel"
                @blur="onFieldBlur('lastName')"
                @keydown="clearFieldError('lastName')"
            />
            <div v-if="errors?.lastName?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.lastName) }}
            </div>
        </div>

        <!-- Email -->
        <div v-if="qrcodeObject?.getUserEmail !== 'no'" class="form-field">
            <input
                id="email-input"
                type="email"
                v-model="email"
                :placeholder="emailLabel"
                @blur="onFieldBlur('email')"
                @input="onEmailInput"
                @keydown="clearFieldError('email')"
            />
            <div v-if="errors?.email?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.email) }}
            </div>
        </div>

        <!-- Phone -->
        <div v-if="qrcodeObject?.getUserPhone !== 'no'" class="form-field">
            <input
                id="phone-input"
                type="tel"
                v-model="phone"
                :placeholder="phoneLabel"
                @blur="onFieldBlur('phone')"
                @keydown="clearFieldError('phone')"
            />
            <div v-if="errors?.phone?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.phone) }}
            </div>
        </div>

        <!-- Video Agreement -->
        <div v-if="qrcodeObject?.getVideoAgreement !== 'no'" class="form-field">
            <CheckBox :text="videoAgreementText" :value="videoAgreement" @input="toggleAgreement" />
            <div v-if="errors?.videoAgreement?.length" class="error-message">
                {{ $t("personalDataForm.errors." + errors.videoAgreement) }}
            </div>
        </div>

        <transition name="fade" mode="out-in">
            <div class="submit-container" v-if="!isPending">
                <input type="submit" :disabled="formInvalid" :value="$t('general.validate')" />
            </div>
            <Loader class="loader" v-else
        /></transition>
    </form>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import Loader from "@/components/Loader.vue";
import CheckBox from "@/components/Checkbox.vue";
import { State } from "@/store";
import { mapState } from "vuex";

const phoneRegex = /^\+?[0-9\s]+$/;

enum FieldNames {
    firstName = "firstName",
    lastName = "lastName",
    email = "email",
    phone = "phone",
    videoAgreement = "videoAgreement"
}

export default defineComponent({
    name: "PersonalDataForm",
    components: {
        Loader,
        CheckBox
    },
    data() {
        return {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            videoAgreement: false,
            consentChecked: false,
            isPending: false,
            errors: {} as { [key: string]: string }
        };
    },
    computed: {
        formInvalid() {
            return Object.keys(FieldNames).find(
                fieldName => fieldName !== "videoAgreement" && this.checkField(fieldName).length > 0
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject
        }),
        firstNameLabel() {
            let label = this.$t("personalDataForm.firstNameLabel");

            if (this.qrcodeObject?.getUserFirstName === "required") {
                label += "*";
            }

            return label;
        },
        lastNameLabel() {
            let label = this.$t("personalDataForm.lastNameLabel");

            if (this.qrcodeObject?.getUserLastName === "required") {
                label += "*";
            }

            return label;
        },
        emailLabel() {
            let label = this.$t("personalDataForm.emailLabel");
            
            if (this.qrcodeObject?.getUserEmail === "required") {
                label += "*";
            }

            return label;
        },
        phoneLabel() {
            let label = this.$t("personalDataForm.phoneLabel");

            if (this.qrcodeObject?.getUserPhone === "required") {
                label += "*";
            }

            return label;
        },
        videoAgreementText() {
            let text = this.qrcodeObject.videoAgreementMsg;

            if (this.qrcodeObject?.getVideoAgreement === "required") {
                text += "*";
            }

            return text;
        }
    },
    methods: {
        async submitForm() {
            const videoAgreementField = this.checkField(FieldNames.videoAgreement);

            if (videoAgreementField) {
                this.errors[FieldNames.videoAgreement] = videoAgreementField;
                return;
            }

            this.$store.dispatch("record/setUserData", {
                firstName: this.firstName,
                lastName: this.lastName,
                email: this.email,
                phone: this.phone,
                videoAgreement: this.videoAgreement
            });
            this.$router.push("Upload");
        },
        clearFieldError(fieldName: string) {
            delete this.errors[fieldName];
        },
        toggleAgreement(value: boolean) {
            this.videoAgreement = value;
            this.clearFieldError(FieldNames.videoAgreement);
        },
        checkField(fieldName: string) {
            let result = "";
            switch (fieldName) {
                case FieldNames.firstName:
                    if (
                        this.qrcodeObject?.getUserFirstName === "required" &&
                        !this.firstName.length
                    ) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.lastName:
                    if (this.qrcodeObject.getUserLastName === "required" && !this.lastName.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.email:
                    if (this.qrcodeObject.getUserEmail === "required" && !this.email.length) {
                        result = "fieldRequired";
                    }
                    break;

                case FieldNames.videoAgreement:
                    if (
                        this.qrcodeObject?.getVideoAgreement === "required" &&
                        !this.videoAgreement
                    ) {
                        result = "videoAgreement";
                    }
                    break;

                case FieldNames.phone:
                    if (this.qrcodeObject.getUserPhone === "required" && !this.phone.length) {
                        result = "fieldRequired";
                    } else if (
                        this.qrcodeObject.getUserPhone !== "no" &&
                        this.phone.length &&
                        !phoneRegex.test(this.phone)
                    ) {
                        result = "fieldValueIncorrect";
                    }
                    break;
            }

            return result;
        },
        onFieldBlur(fieldName: string) {
            const errors = this.checkField(fieldName);
            this.errors[fieldName] = errors;
        },
        onEmailInput(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        }
    }
});
</script>

<style scoped>
h1 {
    margin: 0;
    color: var(--screenTextColor);
}

.user-data-form-container {
    width: 95%;
    align-self: center;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 25px;
    align-items: center;
    justify-items: center;
    margin-top: 25px;
}

.form-field {
    display: flex;
    flex-direction: column;
}

.form-field > *:nth-child(1) {
    margin-bottom: 5px;
}

input[type="email"],
input[type="text"],
input[type="tel"] {
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    background: var(--white);
    color: var(--black);
    border: 0;
    box-shadow: 2px 3px 5px 0px rgb(150 150 150 / 50%);
}

.submit-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

input[type="submit"] {
    appearance: none;
    margin-bottom: 5px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    border: 0;
    background: var(--secondary);
    color: var(--buttonTextColor);
    transition: filter 0.2s ease-in;
}

input[type="submit"]:disabled {
    opacity: 0.5;
}

.loader {
    margin: auto;
}

.error-message {
    color: var(--red);
    font-size: 0.95em;
}
</style>
